import React, { useEffect, useState } from 'react';
import { Link } from "react-router-dom";
import { API_BASE_URL } from '../../assets/AxiosClient';
import AxiosClient from '../../assets/AxiosClient';
import { useStateContext } from '../../Contexts/ContextProvider';

const formatDate = (value) => {
  if (!value) return 'Date not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Date not set';
  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const formatDateTimeInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const getCountdownParts = (value, now) => {
  const target = new Date(value);
  if (!value || Number.isNaN(target.getTime()) || target.getTime() <= now) return null;

  const current = new Date(now);
  let months = (target.getFullYear() - current.getFullYear()) * 12
    + target.getMonth() - current.getMonth();
  const getMonthAnchor = (monthOffset) => {
    const anchor = new Date(current);
    const day = anchor.getDate();
    anchor.setDate(1);
    anchor.setMonth(anchor.getMonth() + monthOffset);
    anchor.setDate(Math.min(day, new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0).getDate()));
    return anchor;
  };

  let anchor = getMonthAnchor(months);
  if (anchor > target) {
    months -= 1;
    anchor = getMonthAnchor(months);
  }

  const remaining = target.getTime() - anchor.getTime();
  const dayInMilliseconds = 24 * 60 * 60 * 1000;
  return {
    months,
    days: Math.floor(remaining / dayInMilliseconds),
    hours: Math.floor((remaining % dayInMilliseconds) / (60 * 60 * 1000)),
    seconds: Math.floor((remaining % 60000) / 1000),
  };
};

const ConferencesRow = ({ conference, status, now }) => {
  const { SetNotification } = useStateContext();
  const [currentConference, setCurrentConference] = useState(conference);
  const [showMailSettings, setShowMailSettings] = useState(false);
  const [showRegistrationSettings, setShowRegistrationSettings] = useState(false);
  const [isSavingMailSettings, setIsSavingMailSettings] = useState(false);
  const [isSavingRegistrationSettings, setIsSavingRegistrationSettings] = useState(false);
  const [clearCredentials, setClearCredentials] = useState(false);
  const [registrationSettings, setRegistrationSettings] = useState({
    registration_open: true,
    registration_deadline: '',
  });
  const [mailSettings, setMailSettings] = useState({
    contact_email: '',
    smtp_host: '',
    smtp_port: '587',
    smtp_encryption: 'tls',
    smtp_username: '',
    smtp_password: '',
    mail_from_address: '',
    mail_from_name: '',
  });

  useEffect(() => {
    setCurrentConference(conference);
  }, [conference]);

  const {
    id,
    en_title,
    ar_title,
    ar_slogan,
    en_slogan,
    start_date,
    end_date,
    location_name,
    ar_location_name,
    latitude,
    longitude,
    logo,
    link,
  } = currentConference;
  const coordinatesAvailable = latitude !== null && latitude !== undefined && longitude !== null && longitude !== undefined;
  const mailSettingsConfigured = Boolean(
    currentConference.contact_email
    && currentConference.smtp_host
    && currentConference.smtp_port
    && currentConference.mail_from_address
    && currentConference.smtp_credentials_configured,
  );
  const registrationDeadline = currentConference.registration_deadline
    ? new Date(currentConference.registration_deadline).getTime()
    : null;
  const hasRegistrationDeadline = registrationDeadline !== null && Number.isFinite(registrationDeadline);
  const registrationIsOpen = currentConference.registration_open !== false
    && (!hasRegistrationDeadline || registrationDeadline > now);
  const countdown = status === 'upcoming'
    ? getCountdownParts(start_date, now)
    : status === 'live'
      ? getCountdownParts(end_date || start_date, now)
      : null;

  const openMailSettings = () => {
    setMailSettings({
      contact_email: currentConference.contact_email || '',
      smtp_host: currentConference.smtp_host || '',
      smtp_port: currentConference.smtp_port ? String(currentConference.smtp_port) : '587',
      smtp_encryption: currentConference.smtp_encryption || 'tls',
      smtp_username: '',
      smtp_password: '',
      mail_from_address: currentConference.mail_from_address || '',
      mail_from_name: currentConference.mail_from_name || '',
    });
    setClearCredentials(false);
    setShowMailSettings(true);
  };

  const updateMailSetting = (event) => {
    const { name, value } = event.target;
    setMailSettings((current) => ({ ...current, [name]: value }));
  };

  const saveMailSettings = async (event) => {
    event.preventDefault();
    setIsSavingMailSettings(true);

    const payload = {
      contact_email: mailSettings.contact_email.trim(),
      smtp_host: mailSettings.smtp_host.trim(),
      smtp_port: Number(mailSettings.smtp_port),
      smtp_encryption: mailSettings.smtp_encryption,
      mail_from_address: mailSettings.mail_from_address.trim(),
      mail_from_name: mailSettings.mail_from_name.trim(),
    };

    if (clearCredentials) {
      payload.smtp_username = null;
      payload.smtp_password = null;
    } else {
      if (mailSettings.smtp_username.trim()) payload.smtp_username = mailSettings.smtp_username.trim();
      if (mailSettings.smtp_password) payload.smtp_password = mailSettings.smtp_password;
    }

    try {
      const response = await AxiosClient.put(`/conferences/${id}`, payload);
      setCurrentConference((current) => ({ ...current, ...response.data }));
      SetNotification('Conference email settings saved', 'success');
      setShowMailSettings(false);
    } catch (error) {
      const message = error.response?.data?.message || 'Could not save conference email settings.';
      SetNotification(message, 'danger');
    } finally {
      setIsSavingMailSettings(false);
    }
  };

  const openRegistrationSettings = () => {
    setRegistrationSettings({
      registration_open: currentConference.registration_open ?? true,
      registration_deadline: formatDateTimeInput(currentConference.registration_deadline),
    });
    setShowRegistrationSettings(true);
  };

  const saveRegistrationSettings = async (event) => {
    event.preventDefault();
    setIsSavingRegistrationSettings(true);

    try {
      const response = await AxiosClient.put(`/conferences/${id}`, {
        registration_open: registrationSettings.registration_open,
        registration_deadline: registrationSettings.registration_deadline || null,
      });
      setCurrentConference((current) => ({ ...current, ...response.data }));
      SetNotification('Registration settings saved', 'success');
      setShowRegistrationSettings(false);
    } catch (error) {
      const message = error.response?.data?.message || 'Could not save registration settings.';
      SetNotification(message, 'danger');
    } finally {
      setIsSavingRegistrationSettings(false);
    }
  };

  return (
    <article className="conference-card">
      <div className="conference-card__topline">
        <div className="conference-card__badges">
          <span className={`conference-card__status conference-card__status--${status}`}>
            <span className="conference-card__status-dot" aria-hidden="true" />
            {status === 'live' ? 'Live now' : status === 'upcoming' ? 'Upcoming' : 'Past'}
          </span>
          <span
            className={`conference-card__registration-status ${registrationIsOpen ? 'conference-card__registration-status--open' : 'conference-card__registration-status--closed'}`}
            aria-label={`Registration ${registrationIsOpen ? 'open' : 'closed'}`}
            title={hasRegistrationDeadline ? `${registrationIsOpen ? 'Closes' : 'Deadline'} ${formatDate(registrationDeadline)}` : undefined}
          >
            <span className="conference-card__registration-dot" aria-hidden="true" />
            Registration {registrationIsOpen ? 'open' : 'closed'}
          </span>
        </div>
        {logo && (
          <img className="conference-card__logo" src={`${API_BASE_URL}${logo}`} alt={`${en_title || ar_title} logo`} />
        )}
      </div>

      <div className="conference-card__content">
        <h3>{en_title || ar_title || 'Untitled conference'}</h3>
        {ar_title && en_title && <p className="conference-card__arabic-title" dir="auto">{ar_title}</p>}
        <div className="conference-card__slogans">
          {en_slogan && <p>{en_slogan}</p>}
          {ar_slogan && <p dir="auto">{ar_slogan}</p>}
        </div>
      </div>

      <dl className="conference-card__details">
        <div>
          <dt><i className="material-icons" aria-hidden="true">event</i> Schedule</dt>
          <dd>{formatDate(start_date)}<span className="conference-card__date-separator">to</span>{formatDate(end_date || start_date)}</dd>
        </div>
        {(location_name || ar_location_name) && (
          <div>
            <dt><i className="material-icons" aria-hidden="true">place</i> Location</dt>
            <dd>
              {location_name && <span>{location_name}</span>}
              {ar_location_name && <span dir="auto">{ar_location_name}</span>}
            </dd>
          </div>
        )}
        {coordinatesAvailable && (
          <div>
            <dt><i className="material-icons" aria-hidden="true">my_location</i> Coordinates</dt>
            <dd>{latitude}, {longitude}</dd>
          </div>
        )}
      </dl>

      <footer className="conference-card__footer">
        {link ? (
          <a href={link} target="_blank" rel="noreferrer" className="conference-card__website">
            Conference website <i className="material-icons" aria-hidden="true">open_in_new</i>
          </a>
        ) : <span />}
        <div className="conference-card__actions">
          <span
            className={`conference-card__mail-status ${mailSettingsConfigured ? 'conference-card__mail-status--ready' : 'conference-card__mail-status--warning'}`}
            title={mailSettingsConfigured ? 'Email connection configured' : 'Email connection needs setup'}
          >
            <i className="material-icons" aria-hidden="true">
              {mailSettingsConfigured ? 'check_circle' : 'warning'}
            </i>
            <span>{mailSettingsConfigured ? 'Email ready' : 'Email setup needed'}</span>
          </span>
          <button
            type="button"
            className="conference-card__icon-button"
            onClick={openRegistrationSettings}
            title="Manage registration"
            aria-label={`Manage registration for ${en_title || ar_title}`}
            aria-haspopup="dialog"
          >
            <i className="material-icons" aria-hidden="true">how_to_reg</i>
          </button>
          <button
            type="button"
            className="conference-card__icon-button"
            onClick={openMailSettings}
            title={mailSettingsConfigured ? 'Edit email connection' : 'Set up email connection'}
            aria-label={mailSettingsConfigured ? `Edit email connection for ${en_title}` : `Set up email connection for ${en_title}`}
          >
            <i className="material-icons" aria-hidden="true">settings</i>
          </button>
          <Link to={`./Edite/${id}`} className="btn btn-outline-dark conference-card__edit">Edit</Link>
        </div>
      </footer>

      {countdown && (
        <div className={`conference-card__countdown conference-card__countdown--${status}`} aria-label={`${status === 'live' ? 'Ends' : 'Starts'} in ${countdown.months} months, ${countdown.days} days, ${countdown.hours} hours, ${countdown.seconds} seconds`}>
          <span className="conference-card__countdown-label">{status === 'live' ? 'Conference ends in' : 'Conference starts in'}</span>
          <div className="conference-card__countdown-units" aria-hidden="true">
            <span><strong>{countdown.months}</strong><small>months</small></span>
            <span><strong>{countdown.days}</strong><small>days</small></span>
            <span><strong>{countdown.hours}</strong><small>hours</small></span>
            <span><strong>{countdown.seconds}</strong><small>seconds</small></span>
          </div>
        </div>
      )}

      {showMailSettings && (
        <div className="conference-mail-modal" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setShowMailSettings(false);
        }}>
          <section className="conference-mail-modal__panel" role="dialog" aria-modal="true" aria-labelledby={`mail-settings-title-${id}`}>
            <header className="conference-mail-modal__header">
              <div>
                <h2 id={`mail-settings-title-${id}`}>Email connection</h2>
                <p>{en_title || ar_title}</p>
              </div>
              <button type="button" className="conference-card__icon-button" onClick={() => setShowMailSettings(false)} title="Close" aria-label="Close email settings">
                <i className="material-icons" aria-hidden="true">close</i>
              </button>
            </header>

            <form onSubmit={saveMailSettings}>
              <div className="conference-mail-modal__body">
                <label>
                  <span>Contact email</span>
                  <input name="contact_email" type="email" value={mailSettings.contact_email} onChange={updateMailSetting} required />
                </label>
                <label>
                  <span>SMTP host</span>
                  <input name="smtp_host" value={mailSettings.smtp_host} onChange={updateMailSetting} required />
                </label>
                <div className="conference-mail-modal__field-row">
                  <label>
                    <span>Port</span>
                    <input name="smtp_port" type="number" min="1" max="65535" value={mailSettings.smtp_port} onChange={updateMailSetting} required />
                  </label>
                  <label>
                    <span>Encryption</span>
                    <select name="smtp_encryption" value={mailSettings.smtp_encryption} onChange={updateMailSetting}>
                      <option value="tls">TLS</option>
                      <option value="ssl">SSL</option>
                      <option value="none">None</option>
                    </select>
                  </label>
                </div>
                <label>
                  <span>SMTP username</span>
                  <input
                    name="smtp_username"
                    autoComplete="username"
                    value={mailSettings.smtp_username}
                    onChange={updateMailSetting}
                    placeholder={currentConference.smtp_credentials_configured ? 'Saved; leave blank to keep it' : ''}
                    required={!currentConference.smtp_credentials_configured && !clearCredentials}
                    disabled={clearCredentials}
                  />
                </label>
                <label>
                  <span>SMTP password</span>
                  <input
                    name="smtp_password"
                    type="password"
                    autoComplete="new-password"
                    value={mailSettings.smtp_password}
                    onChange={updateMailSetting}
                    placeholder={currentConference.smtp_credentials_configured ? 'Saved; leave blank to keep it' : ''}
                    required={!currentConference.smtp_credentials_configured && !clearCredentials}
                    disabled={clearCredentials}
                  />
                </label>
                {currentConference.smtp_credentials_configured && (
                  <label className="conference-mail-modal__checkbox">
                    <input type="checkbox" checked={clearCredentials} onChange={(event) => setClearCredentials(event.target.checked)} />
                    <span>Clear saved SMTP credentials</span>
                  </label>
                )}
                <label>
                  <span>Sender email</span>
                  <input name="mail_from_address" type="email" value={mailSettings.mail_from_address} onChange={updateMailSetting} required />
                </label>
                <label>
                  <span>Sender name</span>
                  <input name="mail_from_name" value={mailSettings.mail_from_name} onChange={updateMailSetting} required />
                </label>
              </div>
              <footer className="conference-mail-modal__footer">
                <button type="button" className="btn btn-outline-dark" onClick={() => setShowMailSettings(false)} disabled={isSavingMailSettings}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={isSavingMailSettings}>
                  <i className="material-icons" aria-hidden="true">save</i>
                  {isSavingMailSettings ? 'Saving...' : 'Save connection'}
                </button>
              </footer>
            </form>
          </section>
        </div>
      )}

      {showRegistrationSettings && (
        <div className="conference-mail-modal conference-registration-modal" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setShowRegistrationSettings(false);
        }}>
          <section className="conference-mail-modal__panel conference-registration-modal__panel" role="dialog" aria-modal="true" aria-labelledby={`registration-settings-title-${id}`}>
            <header className="conference-mail-modal__header">
              <div>
                <h2 id={`registration-settings-title-${id}`}>Registration settings</h2>
                <p>{en_title || ar_title}</p>
              </div>
              <button type="button" className="conference-card__icon-button" onClick={() => setShowRegistrationSettings(false)} title="Close" aria-label="Close registration settings">
                <i className="material-icons" aria-hidden="true">close</i>
              </button>
            </header>

            <form onSubmit={saveRegistrationSettings}>
              <div className="conference-mail-modal__body">
                <label className="conference-mail-modal__checkbox">
                  <input
                    type="checkbox"
                    checked={registrationSettings.registration_open}
                    onChange={(event) => setRegistrationSettings((current) => ({
                      ...current,
                      registration_open: event.target.checked,
                    }))}
                  />
                  <span>Registration is open</span>
                </label>
                <label>
                  <span>Registration deadline</span>
                  <input
                    type="datetime-local"
                    value={registrationSettings.registration_deadline}
                    onChange={(event) => setRegistrationSettings((current) => ({
                      ...current,
                      registration_deadline: event.target.value,
                    }))}
                  />
                </label>
              </div>
              <footer className="conference-mail-modal__footer">
                <button type="button" className="btn btn-outline-dark" onClick={() => setShowRegistrationSettings(false)} disabled={isSavingRegistrationSettings}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={isSavingRegistrationSettings}>
                  <i className="material-icons" aria-hidden="true">save</i>
                  {isSavingRegistrationSettings ? 'Saving...' : 'Save registration'}
                </button>
              </footer>
            </form>
          </section>
        </div>
      )}
    </article>
  );
};

export default ConferencesRow;